import { Modal } from 'components/Modal';
import { Title } from 'components/Title';
import styles from './CreateEmployeeModal.module.scss';
import { Button } from 'ui/Button';
import { Label } from 'components/CreateOrderForm';
import { ReactComponent as InfoIcon } from '../CreateOrderModal/img/iconInfo.svg';
import { ReactComponent as IconDone } from '../CreateOrderModal/img/iconDone.svg';
import { useState, useEffect, useRef } from 'react';
import {
  Group,
  NumberInput,
  TextInput,
  Text,
  Switch,
  Stack,
  Loader,
} from '@mantine/core';
import PhoneForm from 'components/PhoneForm';
import classNames from 'classnames';
import {
  useInviteEmployeeMutation,
  useUpdateEmployeeMutation,
} from 'redux/Api/employeesApi/employeesApi';
import { InviteEmployeeResponse } from 'redux/Api/employeesApi/type';
import { AppTooltip } from 'ui/AppTooltip';

interface FormValues {
  phone: string;
  surname: string;
  name: string;
  patronymic: string;
  can_manage_orders: boolean;
  limit: number | null;
}

const getInitialValues = (): FormValues => ({
  phone: '',
  surname: '',
  name: '',
  patronymic: '',
  can_manage_orders: false,
  limit: null,
});

export const CreateEmployeeModal = ({
  setOpen,
  onSuccess,
}: {
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
}) => {
  const [values, setValues] = useState<FormValues>(getInitialValues());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [invitationError, setInvitationError] = useState('');
  const [update, setUpdate] = useState(false);
  const [userData, setUserData] = useState<Partial<InviteEmployeeResponse>>({});
  const patronymic = userData.patronymic;
  const [isLimitFocused, setIsLimitFocused] = useState(false);

  const [inviteEmployee, { isLoading }] = useInviteEmployeeMutation();
  const [updateEmployee, { isLoading: isLoadingUpdate }] = useUpdateEmployeeMutation();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!values.phone.trim()) {
      newErrors.phone = 'Обязательное поле';
    } else if (values.phone === '7' || values.phone.length < 10) {
      newErrors.phone = 'Некорректный номер телефона';
    }

    if (values.can_manage_orders && values.limit !== null && values.limit <= 0) {
      newErrors.limit = 'Лимит должен быть больше 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const phone = values.phone.startsWith('7') ? values.phone : `7${values.phone}`;

    try {
      const response = await inviteEmployee({
        phone: phone,
        surname: values.surname.trim() || undefined,
        name: values.name.trim() || undefined,
        patronymic: values.patronymic.trim() || undefined,
        can_manage_orders: values.can_manage_orders,
        limit: values.can_manage_orders && values.limit ? values.limit : undefined,
      }).unwrap();

      if (response.status) {
        setValues({
          ...getInitialValues(),
          can_manage_orders: response.can_manage_orders || false,
          limit: response.orders_limit || null,
        });
        setUserData(response);
        setUpdate(true);
        return;
      }

      setValues(getInitialValues());
      setOpen(false);
      onSuccess?.();
    } catch (error) {
      setInvitationError((error as any).data.message);
      console.log('Ошибка отправки приглашения', error);
    }
  };

  const handleUpdate = async () => {
    try {
      await updateEmployee({
        employeeId: Number(userData?.user_id),
        can_manage_orders: values.can_manage_orders,
        limit: values.can_manage_orders && values.limit ? values.limit : null,
      });

      setUserData({});
      setUpdate(false);
      setOpen(false);
    } catch (error) {
      setInvitationError((error as any).data.message);
      console.log('Ошибка отправки приглашения', error);
    }
  };

  const formatRussianPhone = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');

    if (digits.length === 11 && digits.startsWith('7')) {
      const code = digits.slice(1, 4);
      const part1 = digits.slice(4, 7);
      const part2 = digits.slice(7, 9);
      const part3 = digits.slice(9);
      return `+7 (${code}) ${part1}-${part2}-${part3}`;
    }

    return phone;
  };

  return (
    <Modal setOpen={setOpen}>
      <div className={styles.root}>
        <Title className={styles.root_title} title='Новый представитель' />

        {update ? null : (
          <p className={styles.subTitle}>
            Пользователю будет отправлено приглашение к сотрудничеству. Если он не
            зарегистрирован на платформе, ему также придет ссылка для регистрации
          </p>
        )}

        <div>
          <Label text='Номер телефона' />
          <PhoneForm
            value={values.phone}
            onChange={(v) => {
              setValues((prev) => ({ ...prev, phone: v }));
              if (errors.phone) {
                setErrors((prev) => ({ ...prev, phone: '' }));
              }
            }}
            error={errors.phone}
            inputClassName={classNames(
              styles.phoneInput,
              errors.phone && styles.phoneInputError,
            )}
            placeholder={
              update && userData.phone
                ? formatRussianPhone(userData.phone)
                : '+7 (000) 000-00-00'
            }
            disabled={update}
            readOnly={update}
          />
          {errors.phone && <p className={styles.errorPlaceholder}>{errors.phone}</p>}
        </div>
        <div className={styles.text}>
          {update ? 'Пользователь с данным номером уже существует' : null}
        </div>

        <div>
          <Label text='Фамилия' />
          <TextInput
            placeholder={
              update && userData.surname ? `${userData.surname}` : 'Введи фамилию'
            }
            disabled={update}
            value={values.surname}
            onChange={(e) => {
              const filteredValue = e.target.value.replace(/\d/g, '');
              setValues((prev) => ({ ...prev, surname: filteredValue }));
              if (errors.surname) {
                setErrors((prev) => ({ ...prev, surname: '' }));
              }
            }}
            styles={{
              input: {
                height: '48px',
                fontSize: '16px',
                border: 'none',
                borderColor: errors.surname ? '#E10D0D' : undefined,
              },
            }}
            error={!!errors.surname}
          />
          {errors.surname && <p className={styles.errorPlaceholder}>{errors.surname}</p>}
        </div>
        <div>
          <Label text='Имя' />
          <TextInput
            placeholder={update ? `${userData.name}` : 'Введи имя'}
            disabled={update}
            value={values.name}
            onChange={(e) => {
              const filteredValue = e.target.value.replace(/\d/g, '');
              setValues((prev) => ({ ...prev, name: filteredValue }));
              if (errors.name) {
                setErrors((prev) => ({ ...prev, name: '' }));
              }
            }}
            styles={{
              input: {
                height: '48px',
                fontSize: '16px',
                border: 'none',
                borderColor: errors.name ? '#E10D0D' : undefined,
              },
            }}
            error={!!errors.name}
          />
          {errors.name && <p className={styles.errorPlaceholder}>{errors.name}</p>}
        </div>
        <div>
          <Label text='Отчество (если есть)' />
          <TextInput
            placeholder={
              update ? (patronymic ? `${userData.patronymic}` : '') : 'Введи отчество'
            }
            disabled={update}
            value={values.patronymic}
            onChange={(e) => {
              const filteredValue = e.target.value.replace(/\d/g, '');
              setValues((prev) => ({ ...prev, patronymic: filteredValue }));
            }}
            styles={{
              input: { height: '48px', fontSize: '16px', border: 'none' },
            }}
          />
        </div>
        <div className={styles.wrapper}>
          {update ? null : (
            <p className={styles.subTitle}>
              ФИО в соответствии с документом удостоверяющим личность
            </p>
          )}

          <Group gap='8px'>
            <Switch
              color='#002CFB'
              styles={{
                label: {
                  fontSize: '16px',
                  lineHeight: '22px',
                  color: '#121922',
                  cursor: 'pointer',
                },
                track: {
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: values.can_manage_orders
                    ? '#002CFB'
                    : 'rgba(173, 191, 223, 0.40)',
                },
                thumb: {
                  border: 'none',
                },
              }}
              checked={values.can_manage_orders}
              onChange={(event) => {
                const canManage = event.target.checked;
                setValues((prev) => ({
                  ...prev,
                  can_manage_orders: canManage,
                  limit: canManage ? prev.limit : null,
                }));
              }}
              label='С правом подписания договоров, создавать и завершать заказы'
            />
            <AppTooltip label='Пользователь сможет создавать и завершать заказы в рамках баланса лицевого счета'>
              <InfoIcon style={{ cursor: 'pointer' }} />
            </AppTooltip>
          </Group>

          <Group gap='8px' mt='4px'>
            <Switch
              color='#002CFB'
              styles={{
                label: {
                  fontSize: '16px',
                  lineHeight: '22px',
                  color: values.can_manage_orders ? '#121922' : '#ADBFDF',
                  cursor: values.can_manage_orders ? 'pointer' : 'not-allowed',
                },
                track: {
                  border: 'none',
                  cursor: values.can_manage_orders ? 'pointer' : 'not-allowed',
                  backgroundColor:
                    values.can_manage_orders && values.limit !== null
                      ? '#002CFB'
                      : 'rgba(173, 191, 223, 0.40)',
                },
                thumb: {
                  border: 'none',
                },
              }}
              checked={values.can_manage_orders && values.limit !== null}
              onChange={(event) => {
                if (values.can_manage_orders) {
                  setValues((prev) => ({
                    ...prev,
                    limit: event.target.checked ? 0 : null,
                  }));
                }
              }}
              disabled={!values.can_manage_orders}
              label='Лимит'
            />
          </Group>

          {values.can_manage_orders && values.limit !== null && (
            <div style={{ marginTop: '12px' }}>
              <Label text='Лимит на календарный месяц' />
              <NumberInput
                placeholder={!isLimitFocused ? '0' : ''}
                rightSection={<Text c='#ADBFDF'>₽</Text>}
                value={values.limit || ''}
                allowNegative={false}
                decimalScale={0}
                min={0}
                onFocus={() => setIsLimitFocused(true)}
                onBlur={() => setIsLimitFocused(false)}
                onChange={(val) => {
                  setValues((prev) => ({ ...prev, limit: val as number }));
                  if (errors.limit) {
                    setErrors((prev) => ({ ...prev, limit: '' }));
                  }
                }}
                styles={{
                  input: {
                    height: '48px',
                    fontSize: '16px',
                    border: 'none',
                    borderColor: errors.limit ? '#E10D0D' : undefined,
                  },
                }}
                error={!!errors.limit}
              />
              {errors.limit && <p className={styles.errorPlaceholder}>{errors.limit}</p>}
            </div>
          )}
        </div>

        {update ? (
          <div className={styles.info}>
            <div className={styles.info_title}>Доступ отозван</div>
            <div className={styles.info_subtitle}>
              Вы можете восстановить доступ пользователю, а так же изменить его права или
              лимит
            </div>
          </div>
        ) : null}

        <div style={{ color: 'red' }}>{invitationError ? invitationError : null}</div>

        {update ? (
          <Stack gap='8px' mt='24px'>
            <Button
              theme='info'
              size='L'
              className={styles.button}
              onClick={() => {
                if (isLoading) {
                  return;
                }
                handleUpdate();
              }}
              disabled={isLoadingUpdate}>
              <span>
                {isLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Отправка... <Loader size='xs' color='white' />
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Восстановить доступ <IconDone />
                  </div>
                )}
              </span>
            </Button>
          </Stack>
        ) : (
          <Stack gap='8px' mt='24px'>
            <Button
              theme='info'
              className={styles.button}
              size='L'
              onClick={() => {
                if (isLoading) {
                  return;
                }
                handleSave();
              }}>
              <span>
                {isLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Отправка... <Loader size='xs' color='white' />
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Добавить <IconDone />
                  </div>
                )}
              </span>
            </Button>
          </Stack>
        )}
      </div>
    </Modal>
  );
};
